import { Component, inject, signal } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { AuthService } from '@app/services/auth.service';
import { FirebaseError } from 'firebase/app';
import { firstValueFrom } from 'rxjs';
import { User } from 'firebase/auth';
import { customPasswordValidator } from '@app/shared/utils/form';

@Component({
  selector: 'app-change-password',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatIconModule,
    MatButtonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
  ],
  templateUrl: './change-password.component.html',
  styleUrl: './change-password.component.scss',
})
export class ChangePasswordComponent {
  private user: User | null = null;
  isUpdating = signal<boolean>(false);
  status = signal<'initial' | 'verify' | 'reset'>('initial');
  private _formBuilder = inject(FormBuilder);
  initialFormGroup = this._formBuilder.group({
    currentPassword: ['', Validators.required],
  });
  verifyFormGroup = this._formBuilder.group({
    code: ['', Validators.required],
  });
  resetFormGroup = this._formBuilder.group({
    newPassword: ['', [Validators.required, Validators.minLength(8), customPasswordValidator()]],
    confirmPassword: ['', [Validators.required, Validators.minLength(8), customPasswordValidator()]],
  });
  successMessage = '';
  errorMessage = '';

  constructor(
    private authService: AuthService,
  ) {
    this.authService.user$.subscribe((user) => {
      this.user = user;
    });
  }

  async onInitialSubmit() {
    if (!this.initialFormGroup.valid) return;

    this.isUpdating.set(true);

    const formData = this.initialFormGroup.value;
    const email = this.user?.email;
    const password = formData.currentPassword;

    try {
      await firstValueFrom(this.authService.login(email, password));
      await firstValueFrom(this.authService.resendVerificationCode(email));

      this.successMessage = 'Verification code sent to your email';
      this.errorMessage = '';
      this.status.set('verify');
    } catch (error) {
      if (error instanceof FirebaseError) {
        this.errorMessage = error.code === 'auth/user-not-found' ||
            error.code === 'auth/wrong-password'
            ? 'Incorrect current password'
            : 'Something went wrong. Please try again later';
      } else {
        this.errorMessage = 'Something went wrong';
        this.initialFormGroup.reset();
      }
    } finally {
      this.isUpdating.set(false);
    }
  }

  async onVerifySubmit() {
    if (!this.verifyFormGroup.valid) return;
    const formData = this.verifyFormGroup.value;

    this.isUpdating.set(true);

    try {
      await firstValueFrom(
        this.authService.verifyCode(this.user?.email, formData.code),
      );
      this.status.set('reset');
      this.isUpdating.set(false);
      this.verifyFormGroup.reset();
      this.errorMessage = '';
    } catch {
      this.errorMessage = 'Incorrect verification code';
      this.isUpdating.set(false);
    }
  }

  async onResetSubmit() {
    if (!this.resetFormGroup.valid) return;
    const { uid, email } = this.user;
    const formData = this.resetFormGroup.value;

    if (formData.newPassword !== formData.confirmPassword) {
      this.errorMessage = 'Passwords do not match';
      return;
    }

    this.isUpdating.set(true);

    const data: { status?: string } = await firstValueFrom(
      this.authService.updatePassword(uid, formData.newPassword),
    );
    if (data?.status !== 'OK') {
      this.errorMessage = 'Something went wrong';
      return;
    }

    // Login with the new password, as the token is invalidated due to the password change
    await firstValueFrom(this.authService.login(email, formData.newPassword));

    this.successMessage = 'Password updated';
    this.errorMessage = '';

    this.isUpdating.set(false);
    this.status.set('initial');
    this.initialFormGroup.reset();
    this.verifyFormGroup.reset();
    this.resetFormGroup.reset();
  }
}
